import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Linkedin, RefreshCw, Copy, Check, KeyRound, Users, Send,
  Inbox, ExternalLink, AlertCircle, Zap, FileSpreadsheet,
} from "lucide-react";
import LeadDetail from "@/pages/LeadDetail";

export default function LinkedInInbox() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [secret, setSecret] = useState("");
  const [copied, setCopied] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [detail, setDetail] = useState(null);

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/leads/webhook/linkedin`;

  const load = async () => {
    try {
      const reqs = [
        api.get("/leads/linkedin-inbox"),
      ];
      if (isAdmin) {
        reqs.push(api.get("/users"), api.get("/settings/webhook-secret"));
      }
      const results = await Promise.all(reqs);
      setLeads(results[0].data || []);
      if (isAdmin) {
        setMembers((results[1].data || []).filter((m) => m.active));
        setSecret(results[2]?.data?.linkedin_secret || "");
        if (!results[2]?.data?.linkedin_secret) setShowSetup(true);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, []);

  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const assign = async () => {
    if (!assignTo || selected.size === 0) return;
    setAssigning(true);
    try {
      const { data } = await api.post("/leads/bulk-update", {
        ids: Array.from(selected),
        assigned_to: assignTo,
      });
      const target = members.find((m) => m.id === assignTo);
      toast.success(`Assigned ${data.updated} lead${data.updated !== 1 ? "s" : ""} to ${target?.name || "member"}`);
      setSelected(new Set()); setAssignTo("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAssigning(false);
    }
  };

  const regenerate = async () => {
    if (secret && !window.confirm("Regenerate the webhook secret? The old secret will stop working immediately — you'll need to update your Zapier/Make integration.")) return;
    try {
      const { data } = await api.post("/settings/webhook-secret/regenerate");
      setSecret(data.linkedin_secret);
      toast.success(secret ? "Secret regenerated" : "Secret generated — copy it into Zapier now");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <div className="space-y-4" data-testid="linkedin-inbox-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-[#0A66C2] flex items-center justify-center flex-shrink-0">
            <Linkedin className="w-5 h-5 text-white" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{isAdmin ? "SuperAdmin" : "My leads"}</p>
            <h1 className="text-base font-semibold text-slate-900 mt-1">{isAdmin ? "LinkedIn Inbox" : "LinkedIn Leads"}</h1>
            <p className="text-slate-600 mt-0.5 text-sm">
              {loading
                ? "Loading…"
                : isAdmin
                  ? `${leads.length} unassigned lead${leads.length !== 1 ? "s" : ""} — pick a member to allocate.`
                  : `${leads.length} LinkedIn lead${leads.length !== 1 ? "s" : ""} assigned to you.`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button
                onClick={() => navigate("/dashboard/upload?linkedin=1")}
                className="bg-[#0A66C2] hover:bg-[#0955A5] text-white"
                data-testid="import-linkedin-csv-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Import CSV / Excel
              </Button>
              <Button variant="outline" onClick={() => setShowSetup((v) => !v)} className="border-slate-200 bg-slate-100/70" data-testid="toggle-setup-btn">
                <Zap className="w-4 h-4 mr-2" /> {showSetup ? "Hide setup" : "Zapier setup"}
              </Button>
            </>
          )}
          <Button variant="outline" onClick={load} className="border-slate-200 bg-slate-100/70" data-testid="refresh-inbox-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </header>

      {/* Setup / webhook config panel (admin only) */}
      {isAdmin && showSetup && (
        <div className="rounded-xl border border-[#0A66C2]/25 bg-[#0A66C2]/5 p-5" data-testid="linkedin-setup-panel">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-4 h-4 text-[#0A66C2]" />
            <p className="text-sm font-semibold text-slate-800">Zapier / Make webhook</p>
            {!secret && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 uppercase tracking-wider font-medium flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Not configured
              </span>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Webhook URL</p>
              <div className="flex gap-2">
                <input readOnly value={webhookUrl} className="flex-1 h-9 px-3 rounded-md bg-white border border-slate-200 text-xs font-mono text-slate-700" data-testid="webhook-url" />
                <Button variant="outline" size="sm" onClick={() => copy(webhookUrl)} className="border-slate-200">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Secret (header <code className="text-slate-400">X-Webhook-Secret</code>)</p>
              <div className="flex gap-2">
                <input readOnly value={secret || "(not generated yet)"} className="flex-1 h-9 px-3 rounded-md bg-white border border-slate-200 text-xs font-mono text-slate-700" data-testid="webhook-secret" />
                {secret && (
                  <Button variant="outline" size="sm" onClick={() => copy(secret)} className="border-slate-200">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                )}
                <Button size="sm" onClick={regenerate} className="bg-[#0A66C2] hover:bg-[#0955A5] text-white" data-testid="regenerate-secret-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {secret ? "Regenerate" : "Generate"}
                </Button>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Zapier setup — 4 steps</p>
              <ol className="list-decimal ml-4 space-y-1 text-xs text-slate-700">
                <li>Trigger: <span className="font-medium">LinkedIn Ads → New Lead Gen Form Response</span> (connect your LinkedIn Ads account).</li>
                <li>Action: <span className="font-medium">Webhooks by Zapier → POST</span>.</li>
                <li>URL: paste the webhook URL above. Payload type: <span className="font-medium">JSON</span>. Headers: add <code className="bg-white px-1 py-0.5 rounded text-[10px]">X-Webhook-Secret</code> = the secret above.</li>
                <li>Data: map the LinkedIn fields → <code className="bg-white px-1 py-0.5 rounded text-[10px]">name</code>, <code className="bg-white px-1 py-0.5 rounded text-[10px]">phone</code>, <code className="bg-white px-1 py-0.5 rounded text-[10px]">email</code>, <code className="bg-white px-1 py-0.5 rounded text-[10px]">company</code>, <code className="bg-white px-1 py-0.5 rounded text-[10px]">designation</code>, <code className="bg-white px-1 py-0.5 rounded text-[10px]">notes</code>. Turn on the Zap — every new form fill will appear here within seconds.</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Bulk assign bar (admin only) */}
      {isAdmin && selected.size > 0 && (
        <div className="glass rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="li-bulk-assign-bar">
          <span className="text-sm font-medium text-slate-800">{selected.size} selected</span>
          <div className="h-4 w-px bg-slate-200 hidden sm:block" />
          <Select value={assignTo} onValueChange={setAssignTo}>
            <SelectTrigger className="w-56 h-8 bg-white border-slate-200" data-testid="li-assign-select">
              <SelectValue placeholder="Assign to member…" />
            </SelectTrigger>
            <SelectContent>
              {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={assign} disabled={!assignTo || assigning} className="bg-[#0A66C2] hover:bg-[#0955A5] text-white" data-testid="li-assign-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {assigning ? "Assigning…" : "Assign"}
          </Button>
          <button className="ml-auto text-slate-500 hover:text-slate-700" onClick={() => setSelected(new Set())} aria-label="Clear">Clear</button>
        </div>
      )}

      {/* Inbox list */}
      <div className="solid-panel overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-sm text-slate-400">Loading…</p>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center" data-testid="li-empty">
            <Inbox className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="text-sm text-slate-500 mt-2 font-medium">
              {isAdmin ? "Your LinkedIn Inbox is empty" : "No LinkedIn leads assigned to you yet"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {isAdmin
                ? (secret ? "Waiting for the next Zap to fire — new leads appear within seconds." : "Generate a webhook secret above or import a CSV to start receiving leads.")
                : "When your SuperAdmin allocates LinkedIn leads to you, they'll show up here."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="bg-slate-100/70 text-slate-600 text-xs uppercase tracking-widest">
                <tr>
                  {isAdmin && (
                    <th className="text-left px-4 py-3 w-8">
                      <Checkbox checked={selected.size === leads.length && leads.length > 0} onCheckedChange={toggleAll} data-testid="li-select-all" />
                    </th>
                  )}
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-left px-4 py-3">Designation</th>
                  <th className="text-left px-4 py-3">Received</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-slate-200/60 hover:bg-slate-50" data-testid={`li-row-${l.id}`}>
                    {isAdmin && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleOne(l.id)} data-testid={`li-select-${l.id}`} />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button onClick={() => setDetail(l)} className="font-medium text-slate-800 hover:text-[#0A66C2] hover:underline flex items-center gap-1.5">
                        {l.name} <ExternalLink className="w-3 h-3 text-slate-400" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {l.phone && <span>{l.phone}</span>}
                      {l.phone && l.email && <span className="text-slate-400"> · </span>}
                      {l.email && <span>{l.email}</span>}
                      {!l.phone && !l.email && <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{l.company || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-slate-700">{l.designation || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LeadDetail
        lead={detail}
        open={!!detail}
        onOpenChange={(v) => !v && setDetail(null)}
        members={members}
        onChanged={load}
      />
    </div>
  );
}
